/**
 * NewForm design-system PageHeader.
 *
 * The standard page-level header used across the app: eyebrow (breadcrumb-
 * style category label), page title, optional page description, and
 * optional right-aligned actions slot.
 *
 * THE TYPE COMES FROM THE ROLES, which is the whole point of the component.
 * It used to hand-write the title as `text-[28px] font-[600]` and the subtitle
 * as `text-[13px]` — so the one piece of shared furniture meant to keep every
 * page consistent was itself four arbitrary values, and a page that reached
 * for the roles instead did not match it. `Display` is the page title, once
 * per page, and it renders the `h1`; `Lede` is the standfirst under it.
 *
 * The subtitle is deliberately not small. It is 17px and muted, because the
 * hierarchy against the title is carried by colour — see "Every page opens the
 * same way" in the design-system guidelines.
 *
 *   import { PageHeader } from "@/components/ui/design-system/page-header";
 *
 *   <PageHeader
 *     eyebrow="Creative Testing · Batch Details"
 *     title="Batch Details"
 *     subtitle="Monitor and analyze the performance of your creative test batch."
 *     actions={<MyActions />}
 *   />
 *
 * The page title/subtitle are *page identity* — keep them static per route.
 * Dynamic record-specific content (a batch name, a campaign, etc.) belongs
 * in a section below the header, not in the title.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Eyebrow, type EyebrowSegment } from "./eyebrow";
import { Display, Lede } from "./text";

interface PageHeaderProps {
  eyebrow?: ReactNode;
  eyebrowSegments?: EyebrowSegment[];
  eyebrowShowDot?: boolean;
  /** Optional right-aligned content on the same row as the eyebrow (e.g. a status badge). */
  eyebrowEnd?: ReactNode;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /**
   * Where the header sits on its column.
   *
   * `start` everywhere, and that is the rule: every page opens the same way.
   * `center` exists for the one shape that genuinely wants it, a screen whose
   * whole content is a single prompt with nothing else on it. That page has
   * no second thing for the eye to go to, so a title pinned to the top-left
   * corner leaves the window empty below it, and every product with that
   * screen has converged on the same centred composition.
   *
   * It is a variant of this component rather than two pages hand-rolling
   * `Display` and `Lede`, so the type stays one decision and a page that
   * centres cannot drift from a page that does not.
   */
  align?: "start" | "center";
  className?: string;
  /**
   * Keep the actions beside the title at every width.
   *
   * The default stacks them below `lg`, which is right for the common
   * header: a title, a standfirst that wraps, and an action wide enough
   * that a phone row would squeeze both. It is wrong for a header that is
   * one word and one small button, where stacking spends a whole row
   * saying nothing — Inbox is the case. Opt-in rather than measured,
   * because whether the pair fits is a fact about the specific words.
   */
  inlineActions?: boolean;
}

export function PageHeader({
  eyebrow,
  eyebrowSegments,
  eyebrowShowDot = true,
  eyebrowEnd,
  title,
  subtitle,
  actions,
  align = "start",
  className,
  inlineActions = false,
}: PageHeaderProps) {
  const centred = align === "center";
  const eyebrowContent = eyebrowSegments ? (
    <Eyebrow segments={eyebrowSegments} showDot={eyebrowShowDot} />
  ) : eyebrow ? (
    <Eyebrow showDot={eyebrowShowDot}>{eyebrow}</Eyebrow>
  ) : null;

  return (
    <div className={cn("space-y-4", centred && "text-center", className)}>
      {(eyebrowContent || eyebrowEnd) && (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">{eyebrowContent}</div>
          {eyebrowEnd ? <div className="shrink-0">{eyebrowEnd}</div> : null}
        </div>
      )}
      <div
        className={cn(
          "flex flex-col justify-between gap-4",
          centred
            ? "items-center"
            : inlineActions
              ? "flex-row items-center justify-between gap-3"
              : "items-start lg:flex-row lg:items-end",
        )}
      >
        {/* 8 between title and lede when centred, against the 4 a left-aligned
            header uses. A centred pair has no shared edge holding it together,
            so the gap is the only thing saying the two lines are one block,
            and at 4 they read as one crowded paragraph. */}
        <div
          className={cn("min-w-0 flex-1", centred ? "space-y-2" : "space-y-1")}
        >
          <Display>{title}</Display>
          {/* `Lede` carries `max-w-prose`, which is left-anchored by default
              and would hang the standfirst off-centre under the title. */}
          {subtitle ? (
            <Lede muted className={centred ? "mx-auto" : undefined}>
              {subtitle}
            </Lede>
          ) : null}
        </div>
        {actions ? (
          <div className="min-w-0 max-w-full lg:flex-shrink-0">{actions}</div>
        ) : null}
      </div>
    </div>
  );
}
